import "./reports.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";
import API_BASE_URL from "../../../config/api";
import { Modal, Button } from "react-bootstrap";

const flagOptions = ["pending", "reviewed", "resolved", "banned"];
const flagColors = { pending: "flagPending", reviewed: "flagReviewed", resolved: "flagResolved", banned: "flagBanned" };

const banDurations = [
  { key: "1d", label: "1 Day" },
  { key: "3d", label: "3 Days" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "permanent", label: "Permanent" },
];

function formatMsgTime(time) {
  const ms = Number(time);
  if (!ms) return "";
  const t = ms < 1e12 ? ms * 1000 : ms;
  return new Date(t).toLocaleString();
}

function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [banDuration, setBanDuration] = useState("7d");
  const [banReason, setBanReason] = useState("");
  const [banning, setBanning] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [unbanTarget, setUnbanTarget] = useState(null);
  const { token } = useAuth();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/reports`, {
        params: { page, pageSize: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || {};
      setReports(Array.isArray(data.items) ? data.items : []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load reports");
      console.error(err.message);
    }
    setLoading(false);
  }, [token, page]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleChangeFlag = async (id, flag) => {
    try {
      await axios.put(`${API_BASE_URL}/api/v1/reports/${id}`, { flag }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setReports((prev) => prev.map((r) => (r.key === id ? { ...r, flag } : r)));
      if (selected?.key === id) setSelected({ ...selected, flag });
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteTarget(null);
      fetchReports();
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleBan = async () => {
    if (!banTarget) return;
    setBanning(true);
    try {
      await axios.post(`${API_BASE_URL}/api/v1/reports/ban`, {
        offenderid: banTarget.offenderid,
        duration: banDuration,
        reason: banReason || undefined,
        reportId: reportId(banTarget),
      }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setBanning(false);
      setBanTarget(null);
      setBanReason("");
      setSelected(null);
      fetchReports();
    } catch (err) {
      setBanning(false);
      console.error(err.message);
    }
  };

  const handleUnban = async (r) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/reports/unban`, { offenderid: r.offenderid }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setUnbanTarget(null);
      setSelected(null);
      fetchReports();
    } catch (err) {
      console.error(err.message);
    }
  };

  const reportId = (r) => r.key || r.id;

  return (
    <div>
      <div className="reportsHeader">
        <h3>Reported Users</h3>
        <button className="reportsRefreshBtn" onClick={fetchReports}>
          Refresh
        </button>
      </div>

      {loading && <div className="reportsEmpty"><p>Loading reports...</p></div>}

      {error && (
        <div className="reportsError">
          <strong>Error:</strong> {error}
          <br />
          <button onClick={fetchReports}>Retry</button>
        </div>
      )}

      {!loading && !error && reports.length === 0 && (
        <div className="reportsEmpty">
          <h4>No reports yet</h4>
          <p>Reports submitted from the mobile app will appear here.</p>
        </div>
      )}

      {!loading && reports.length > 0 && (
        <div className="reportsTable">
          <table>
            <thead>
              <tr>
                <th>Offender</th>
                <th>Offender ID</th>
                <th>Offence</th>
                <th>Status</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={reportId(r)}>
                  <td>
                    <div className="offenderCell">
                      <div className="offenderAvatar">
                        {r.offender?.photo ? (
                          <img src={r.offender.photo} alt="" />
                        ) : (
                          (r.offender?.username || "?")[0].toUpperCase()
                        )}
                      </div>
                      <span>{r.offender?.username || r.offenderid || "Unknown"}</span>
                      {r.offender?.banned && <span className="bannedBadge">Banned</span>}
                    </div>
                  </td>
                  <td className="offenderIdCell">{r.offenderid || "-"}</td>
                  <td className="reportsBodyCell">{(r.offence || "").substring(0, 60)}{r.offence?.length > 60 ? "..." : ""}</td>
                  <td>
                    <select
                      className={`flagSelect ${flagColors[r.flag] || ""}`}
                      value={r.flag || "pending"}
                      onChange={(e) => handleChangeFlag(reportId(r), e.target.value)}
                    >
                      {flagOptions.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="reportsActionBtn" onClick={() => setSelected(r)}>View</button>
                    {r.offender?.banned && (
                      <button className="reportsUnbanBtn" onClick={() => setUnbanTarget(r)}>Unban</button>
                    )}
                    <button className="reportsDeleteBtn" onClick={() => setDeleteTarget(reportId(r))}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && reports.length > 0 && (
        <div className="reportsPagination">
          <span>
            Page {page} of {totalPages} &middot; {total} report{total === 1 ? "" : "s"}
          </span>
          <div className="reportsPaginationBtns">
            <button className="reportsPageBtn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
            <button className="reportsPageBtn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}

      <Modal show={!!selected} onHide={() => setSelected(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Report Details</Modal.Title>
        </Modal.Header>
        {selected && (
          <Modal.Body>
            <div className="reportDetailRow"><strong>Offender:</strong> {selected.offender?.username || selected.offenderid || "Unknown"}</div>
            <div className="reportDetailRow"><strong>Offender ID:</strong> {selected.offenderid || "-"}</div>
            <div className="reportDetailRow"><strong>Status:</strong> <span className={`statusBadge ${flagColors[selected.flag] || ""}`}>{selected.flag || "pending"}</span></div>
            <div className="reportDetailRow"><strong>Report ID:</strong> {selected.key || selected.id || "-"}</div>
            {selected.offender?.banned && (
              <div className="reportDetailRow">
                <strong>Ban:</strong>{" "}
                {selected.offender.banUntil
                  ? `Until ${new Date(selected.offender.banUntil).toLocaleString()}`
                  : "Permanent"}
                {selected.offender.banReason ? ` — ${selected.offender.banReason}` : ""}
              </div>
            )}
            <div className="reportDetailBody">
              <strong>Offence:</strong>
              <p>{selected.offence || "-"}</p>
            </div>
            {Array.isArray(selected.attachedMessages) && selected.attachedMessages.length > 0 && (
              <div className="reportMessages">
                <div className="reportMessagesTitle">Attached Messages ({selected.attachedMessages.length})</div>
                {selected.attachedMessages.map((m, i) => (
                  <div key={i} className="reportMsg">
                    <div className="reportMsgHeader">
                      <div className="reportMsgSender">
                        <span className="reportMsgAvatar">
                          {m.sender?.photo ? (
                            <img src={m.sender.photo} alt="" />
                          ) : (
                            (m.sender?.username || m.from || "?")[0].toUpperCase()
                          )}
                        </span>
                        <span>{m.sender?.username || m.from || "Unknown"}</span>
                      </div>
                      <span className="reportMsgTime">
                        {m.messageType ? <span className="reportMsgType">{m.messageType}</span> : null}
                        {m.time ? formatMsgTime(m.time) : ""}
                      </span>
                    </div>
                    {m.imageurl && <img className="reportMsgImg" src={m.imageurl} alt="message attachment" />}
                    {m.message && <p className="reportMsgText">{m.message}</p>}
                    {m.multiMedia && m.multiMedia.length > 0 && (
                      <div className="reportMsgMedia">
                        {m.multiMedia.map((mm, j) => mm?.url ? <img key={j} className="reportMsgImg" src={mm.url} alt="" /> : null)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
        )}
        <Modal.Footer style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {flagOptions.map((f) => (
              selected && selected.flag !== f && (
                <Button
                  key={f}
                  variant={f === "banned" ? "danger" : f === "resolved" ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => handleChangeFlag(selected.key || selected.id, f)}
                >
                  Mark {f}
                </Button>
              )
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {selected?.offender?.banned ? (
              <Button variant="outline-success" size="sm" onClick={() => setUnbanTarget(selected)}>Unban</Button>
            ) : (
              <Button variant="danger" size="sm" onClick={() => { setBanDuration("7d"); setBanReason(""); setBanTarget(selected); }}>Ban Account</Button>
            )}
            <Button variant="outline-secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={!!banTarget} onHide={() => setBanTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Ban Account</Modal.Title></Modal.Header>
        <Modal.Body>
          {banTarget && (
            <>
              <p>
                Ban <strong>{banTarget.offender?.username || banTarget.offenderid}</strong> for how long?
              </p>
              <div className="banDurationOptions">
                {banDurations.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    className={`banDurationBtn ${banDuration === d.key ? "active" : ""}`}
                    onClick={() => setBanDuration(d.key)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <div className="banFormGroup">
                <label>Reason (optional)</label>
                <input
                  type="text"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="e.g. Spamming, abusive language"
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBanTarget(null)}>Cancel</Button>
          <Button variant="danger" disabled={banning} onClick={handleBan}>
            {banning ? "Banning..." : "Confirm Ban"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete Report</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete this report?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleDelete(deleteTarget)}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!unbanTarget} onHide={() => setUnbanTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Unban Account</Modal.Title></Modal.Header>
        <Modal.Body>
          {unbanTarget && (
            <>
              Are you sure you want to unban <strong>{unbanTarget.offender?.username || unbanTarget.offenderid}</strong>?
              <br />
              This will re-enable their sign-in access immediately.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setUnbanTarget(null)}>Cancel</Button>
          <Button variant="success" onClick={() => handleUnban(unbanTarget)}>Unban</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Reports;
